// Admin Batches — filterable list of inventory batches across all locations
import React, { useEffect, useState } from "react";
import { Search, Boxes, MapPin, Clock, AlertTriangle, ArrowRightLeft, Trash, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const STATUS_TONE = {
    available: "bg-emerald-100 text-emerald-700",
    quarantine: "bg-amber-100 text-amber-800",
    damaged: "bg-rose-100 text-rose-700",
    expired: "bg-zinc-200 text-zinc-700",
    recalled: "bg-rose-200 text-rose-900",
};

const TransferDialog = ({ batch, bins, onClose, onDone }) => {
    const [toLoc, setToLoc] = useState("");
    const [busy, setBusy] = useState(false);
    if (!batch) return null;
    const submit = async () => {
        if (!toLoc) { toast.error("Pick a destination bin"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/inventory/batches/${batch.id}/transfer`, { to_location_id: toLoc });
            toast.success("Batch transferred");
            onDone();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Transfer failed");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="admin-batch-transfer-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="label-uppercase">Transfer batch</p>
                        <h2 className="text-base font-semibold">{batch.batch_no}</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                </div>
                <p className="mt-1 text-xs text-zinc-500">Current bin: <strong>{batch.location_code || "—"}</strong></p>
                <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Destination bin</label>
                <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm" data-testid="admin-batch-transfer-target">
                    <option value="">— Select bin —</option>
                    {bins.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                </select>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50" data-testid="admin-batch-transfer-submit">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}
                        Transfer
                    </button>
                </div>
            </div>
        </div>
    );
};

const DamageDialog = ({ batch, onClose, onDone }) => {
    const [form, setForm] = useState({ qty: 1, reason: "broken", notes: "", write_off_value: "" });
    const [busy, setBusy] = useState(false);
    if (!batch) return null;
    const submit = async () => {
        if (!form.qty || form.qty < 1) { toast.error("Quantity required"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/inventory/batches/${batch.id}/mark-damaged`, {
                qty: parseInt(form.qty, 10),
                reason: form.reason,
                notes: form.notes,
                write_off_value: parseFloat(form.write_off_value || 0),
            });
            toast.success("Marked as damaged");
            onDone();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Failed");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="admin-batch-damage-modal">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="label-uppercase">Mark damaged</p>
                        <h2 className="text-base font-semibold">{batch.batch_no}</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                </div>
                <p className="mt-1 text-xs text-zinc-500">Available: <strong>{batch.qty_remaining ?? batch.qty_received}</strong></p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Qty damaged</span>
                        <input type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-batch-damage-qty" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Reason</span>
                        <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-batch-damage-reason">
                            <option value="broken">Broken</option>
                            <option value="expired">Expired</option>
                            <option value="leaked">Leaked</option>
                            <option value="temperature">Temperature exposure</option>
                            <option value="other">Other</option>
                        </select>
                    </label>
                    <label className="col-span-2 block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Notes</span>
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 p-2" data-testid="admin-batch-damage-notes" />
                    </label>
                    <label className="col-span-2 block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Write-off value (₹)</span>
                        <input type="number" min="0" step="0.01" value={form.write_off_value} onChange={(e) => setForm({ ...form, write_off_value: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5" data-testid="admin-batch-damage-value" />
                    </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50" data-testid="admin-batch-damage-submit">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
                        Mark damaged
                    </button>
                </div>
            </div>
        </div>
    );
};

const Content = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [productQ, setProductQ] = useState("");
    const [productHits, setProductHits] = useState([]);
    const [filters, setFilters] = useState({ product_id: "", location_id: "", batch_status: "", expiring_in_days: "" });
    const [bins, setBins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [transferFor, setTransferFor] = useState(null);
    const [damageFor, setDamageFor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: 30 };
            if (filters.product_id) params.product_id = filters.product_id;
            if (filters.location_id) params.location_id = filters.location_id;
            if (filters.batch_status) params.batch_status = filters.batch_status;
            if (filters.expiring_in_days) params.expiring_in_days = parseInt(filters.expiring_in_days, 10);
            const { data } = await api.get("/admin/inventory/batches-v2", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page]);

    useEffect(() => {
        api.get("/admin/inventory/locations", { params: { type: "bin" } }).then((r) => setBins(r.data.items || []));
    }, []);

    const searchProducts = async (e) => {
        e.preventDefault();
        if (!productQ.trim()) { setProductHits([]); return; }
        const { data } = await api.get("/products", { params: { q: productQ.trim(), page_size: 8 } });
        setProductHits(data.items || []);
    };

    return (
        <div className="space-y-5" data-testid="admin-batches-page">
            <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-soft">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px]">
                        <p className="label-uppercase">Product filter</p>
                        <form onSubmit={searchProducts} className="mt-1 flex gap-2">
                            <input value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Search product…" className="flex-1 rounded-md border border-zinc-200 px-3 py-1.5 text-sm" data-testid="admin-batches-search" />
                            <button type="submit" className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs"><Search className="h-3 w-3 inline" /></button>
                        </form>
                        {productHits.length > 0 && (
                            <ul className="mt-1 max-h-32 overflow-y-auto rounded-md border border-zinc-100 text-xs">
                                {productHits.map((p) => (
                                    <li key={p.id} className="flex items-center justify-between border-b border-zinc-50 px-2 py-1.5 last:border-b-0">
                                        <span>{p.name}</span>
                                        <button onClick={() => { setFilters({ ...filters, product_id: p.id }); setProductHits([]); setProductQ(p.name); }} className="rounded-md bg-brand-700 px-2 py-0.5 text-[10px] text-white">Filter</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {filters.product_id && (
                            <button onClick={() => { setFilters({ ...filters, product_id: "" }); setProductQ(""); }} className="mt-1 text-[10px] text-zinc-500 hover:text-rose-600">Clear product</button>
                        )}
                    </div>
                    <label className="block">
                        <span className="label-uppercase">Bin</span>
                        <select value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })} className="mt-1 rounded-md border border-zinc-200 px-3 py-1.5 text-sm" data-testid="admin-batches-bin-filter">
                            <option value="">All bins</option>
                            {bins.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-uppercase">Status</span>
                        <select value={filters.batch_status} onChange={(e) => setFilters({ ...filters, batch_status: e.target.value })} className="mt-1 rounded-md border border-zinc-200 px-3 py-1.5 text-sm" data-testid="admin-batches-status-filter">
                            <option value="">Any</option>
                            <option value="available">Available</option>
                            <option value="quarantine">Quarantine</option>
                            <option value="damaged">Damaged</option>
                            <option value="expired">Expired</option>
                            <option value="recalled">Recalled</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-uppercase">Expiring in (days)</span>
                        <input type="number" min="0" value={filters.expiring_in_days} onChange={(e) => setFilters({ ...filters, expiring_in_days: e.target.value })} placeholder="e.g. 60" className="mt-1 w-32 rounded-md border border-zinc-200 px-3 py-1.5 text-sm" data-testid="admin-batches-expiring-filter" />
                    </label>
                    <p className="ml-auto text-xs text-zinc-500">{total} batch(es)</p>
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                <table className="w-full text-sm" data-testid="admin-batches-table">
                    <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                            <th className="px-4 py-3">Product</th>
                            <th className="px-4 py-3">Batch</th>
                            <th className="px-4 py-3">Bin</th>
                            <th className="px-4 py-3 text-right">Qty</th>
                            <th className="px-4 py-3">Expiry</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-10 text-center"><div className="h-6 skeleton rounded mx-auto w-40" /></td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="admin-batches-empty"><Boxes className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-2">No batches match.</p></td></tr>
                        ) : items.map((b) => (
                            <tr key={b.id} className="hover:bg-zinc-50" data-testid={`admin-batches-row-${b.id}`}>
                                <td className="px-4 py-3 text-xs font-medium">{b.product_name || b.product_id?.slice(0, 8)}</td>
                                <td className="px-4 py-3 font-mono text-xs">{b.batch_no || b.id.slice(0, 8)}</td>
                                <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5"><MapPin className="h-3 w-3 text-zinc-400" /> {b.location_code || "—"}</span></td>
                                <td className="px-4 py-3 text-right text-sm tabular-nums font-medium">{b.qty_remaining ?? b.qty_received}</td>
                                <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1 text-zinc-700"><Clock className="h-3 w-3 text-zinc-400" /> {b.expiry_date || "—"}</span></td>
                                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[b.batch_status] || "bg-zinc-100 text-zinc-700"}`}>{b.batch_status || "available"}</span></td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-1">
                                        {(b.batch_status === "available" || !b.batch_status) && (
                                            <>
                                                <button onClick={() => setTransferFor(b)} className="rounded-md border border-zinc-200 px-2 py-1 text-[11px] hover:bg-zinc-50" data-testid={`admin-batch-transfer-${b.id}`}><ArrowRightLeft className="h-3 w-3 inline" /> Move</button>
                                                <button onClick={() => setDamageFor(b)} className="rounded-md border border-rose-200 px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-50" data-testid={`admin-batch-damage-${b.id}`}><Trash className="h-3 w-3 inline" /> Damage</button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!loading && total > 30 && (
                    <div className="flex items-center justify-between border-t border-zinc-100 px-4 py-3 text-xs text-zinc-500">
                        <span>Page {page} of {Math.ceil(total / 30)}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-zinc-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 30 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-zinc-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>

            <TransferDialog batch={transferFor} bins={bins} onClose={() => setTransferFor(null)} onDone={load} />
            <DamageDialog batch={damageFor} onClose={() => setDamageFor(null)} onDone={load} />
        </div>
    );
};

const AdminBatches = () => (
    <AdminAuthGuard>
        <AdminLayout title="Inventory batches" subtitle="FIFO batches across all bins · transfer, damage, recall">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminBatches;
